import { useEffect, useState } from 'react';
import { ActionIcon, Badge, Group, Loader, Stack } from '@mantine/core';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import HttpClient from '../helpers/HttpClient';
import { PaymentAccount } from '../types/payment-account.type';
import { ConfirmDialog } from '../components/ConfirmDialog';

function PaymentAccountList() {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<PaymentAccount[]>([]);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await new HttpClient<PaymentAccount>('/payment-account').getAll();
      setAccounts([...result].sort((a, b) => a.name.localeCompare(b.name)));
    } catch {
      notifications.show({ message: 'Fehler beim Laden der Zahlungskonten', color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async () => {
    if (confirmId === null) return;
    try {
      const res = await new HttpClient<PaymentAccount>('/payment-account').delete(confirmId);
      if (res.status !== 200) throw new Error();
      setAccounts((prev) => prev.filter((a) => Number(a.id) !== confirmId));
      notifications.show({ message: 'Zahlungskonto gelöscht', color: 'green' });
    } catch {
      notifications.show({ message: 'Löschen fehlgeschlagen', color: 'red' });
    } finally {
      setConfirmId(null);
    }
  };

  return (
    <Stack maw={900}>
      <ConfirmDialog
        opened={confirmId !== null}
        onClose={() => setConfirmId(null)}
        onConfirm={handleDelete}
        title="Zahlungskonto löschen"
        message="Soll dieses Zahlungskonto wirklich gelöscht werden?"
        confirmLabel="Löschen"
        cancelLabel="Abbrechen"
      />
      <Group>
        <ActionIcon
          variant="filled"
          size="xl"
          radius="xl"
          aria-label="Neues Zahlungskonto"
          onClick={() => navigate('/payment-account/new')}
        >
          <IconPlus style={{ width: '70%', height: '70%' }} stroke={1.5} />
        </ActionIcon>
      </Group>
      {loading ? (
        <Loader size="md" />
      ) : (
        <DataTable
          withTableBorder
          borderRadius="sm"
          withColumnBorders
          striped
          highlightOnHover
          records={accounts}
          idAccessor="id"
          columns={[
            {
              accessor: 'name',
              title: 'Bezeichnung',
              render: (a) => (
                <Group gap={6}>
                  {a.name}
                  {a.isDefault && <Badge size="sm" color="blue" variant="light">Standard</Badge>}
                </Group>
              ),
            },
            { accessor: 'iban', title: 'IBAN' },
            { accessor: 'bic', title: 'BIC' },
            {
              accessor: 'actions',
              title: '',
              width: '8%',
              render: (a) => (
                <Group gap={4} justify="center">
                  <ActionIcon
                    variant="light"
                    color="blue"
                    onClick={() => navigate(`/payment-account/edit/${a.id}`)}
                    aria-label="Bearbeiten"
                  >
                    <IconPencil size={18} />
                  </ActionIcon>
                  <ActionIcon
                    variant="light"
                    color="red"
                    onClick={() => setConfirmId(Number(a.id))}
                    aria-label="Löschen"
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                </Group>
              ),
            },
          ]}
        />
      )}
    </Stack>
  );
}

export default PaymentAccountList;
